import { UserEntity } from "../../domain/entities/User";
import { CompanyEntity } from "../../domain/entities/Company";
import { AuthResultEntity } from "../../domain/entities/AuthResult";
import { UserDTO, CompanyDTO, LoginResponseDTO } from "../models/AuthDTOs";

/**
 * Auth Mapper
 * Maps between API DTOs and Domain Entities
 */
export class AuthMapper {
  /**
   * Map UserDTO to UserEntity
   */
  static toUserEntity(dto: UserDTO): UserEntity {
    return {
      id: dto.id,
      email: dto.email,
      userName: dto.userName,
      role: dto.role,
      isActive: this.normalizeBoolean(dto.is_active),
      dateCreated: dto.date_created,
      dateLastLoggedIn: dto.date_last_logged_in,
      zohoRefreshToken: dto.zoho_refresh_token,
    };
  }

  /**
   * Map UserEntity to UserDTO (for storage)
   */
  static toUserDTO(user: UserEntity): UserDTO {
    return {
      id: user.id,
      email: user.email,
      userName: user.userName,
      role: user.role,
      is_active: user.isActive,
      date_created: user.dateCreated,
      date_last_logged_in: user.dateLastLoggedIn,
      zoho_refresh_token: user.zohoRefreshToken,
    };
  }

  /**
   * Map CompanyDTO to CompanyEntity
   */
  static toCompanyEntity(dto: CompanyDTO): CompanyEntity {
    const rawUnit = (dto.amount_compact_unit ?? "").toString().toLowerCase();
    const normalizedUnit =
      rawUnit === "whole" ||
      rawUnit === "thousand" ||
      rawUnit === "lakh" ||
      rawUnit === "million" ||
      rawUnit === "crore" ||
      rawUnit === "billion"
        ? rawUnit
        : rawUnit === "crores"
          ? "crore"
          : rawUnit === "lakhs"
            ? "lakh"
            : "lakh";

    const decimalsRaw =
      dto.amount_compact_decimals !== undefined && dto.amount_compact_decimals !== null
        ? Number(dto.amount_compact_decimals)
        : 2;
    const decimals: 1 | 2 = decimalsRaw === 1 ? 1 : 2;

    return {
      companyId: dto.company_id,
      companyName: dto.company_name,
      zohoOrgId: dto.zoho_org_id,
      addressLine1: dto.address_line1,
      addressLine2: dto.address_line2,
      companyGstEnabled: dto.company_gst_enabled,
      gstEnabledFrom: dto.gst_enabled_from ?? null,
      state: dto.state,
      country: dto.country,
      pincode: dto.pincode,
      gstNumber: dto.gst_number,
      panNumber: dto.pan_number,
      cinNumber: dto.cin_number,
      currency: (dto.currency || "INR").trim(),
      msme: dto.msme,
      amountCompactUnit: normalizedUnit as CompanyEntity["amountCompactUnit"],
      amountCompactDecimals: decimals,
      amountCompactSuffixJson: dto.amount_compact_suffix_json ?? null,
      accountingBackend:
        dto.accounting_backend === 'tally' ? 'tally' : 'zoho',
      createdAt: dto.created_at,
    };
  }

  /**
   * Map CompanyEntity to CompanyDTO (for storage)
   */
  static toCompanyDTO(company: CompanyEntity): CompanyDTO {
    return {
      company_id: company.companyId,
      company_name: company.companyName,
      zoho_org_id: company.zohoOrgId,
      address_line1: company.addressLine1,
      address_line2: company.addressLine2,
      company_gst_enabled: company.companyGstEnabled,
      gst_enabled_from: company.gstEnabledFrom ?? null,
      state: company.state,
      country: company.country,
      pincode: company.pincode,
      gst_number: company.gstNumber,
      pan_number: company.panNumber,
      cin_number: company.cinNumber,
      currency: company.currency,
      msme: company.msme,
      amount_compact_unit: company.amountCompactUnit,
      amount_compact_decimals: company.amountCompactDecimals,
      amount_compact_suffix_json: company.amountCompactSuffixJson ?? null,
      accounting_backend: company.accountingBackend ?? 'zoho',
      created_at: company.createdAt,
    };
  }

  /**
   * Map LoginResponseDTO to AuthResultEntity
   */
  static toAuthResult(dto: LoginResponseDTO): AuthResultEntity {
    return {
      user: this.toUserEntity(dto.data.user),
      company: this.toCompanyEntity(dto.data.company),
      token: dto.data.token,
    };
  }

  /**
   * Helper to normalize boolean values
   */
  private static normalizeBoolean(value: "0" | "1" | 0 | 1 | boolean): boolean {
    if (typeof value === "boolean") return value;
    if (value === "1" || value === 1) return true;
    return false;
  }
}
